import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBatchProcesses, deleteBatchProcess } from '../../redux/actions/batchInventoryProcess/batchInventoryProcessAction';
import { fetchDepartments } from '../../redux/actions/department/departmentAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  CircularProgress,
  Typography,
  TablePagination,
  TableSortLabel,
  TextField,
  Autocomplete,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { confirmDelete } from '../../utils/confirmDelete';
//import AddIcon from '@mui/icons-material/Add';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const DEFAULT_LIMIT = 200;

const InventoryProcessTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { processes, loading, error } = useSelector((state) => state.batchInventoryProcess);
  const { departments } = useSelector((state) => state.department);
  const { employees } = useSelector((state) => state.employee);
  const { purities } = useSelector((state) => state.purity);
  const { user } = useSelector((state) => state.login);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id'); // Default sort by id asc
  // Search
  const [search, setSearch] = useState('');
  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [toDept, setToDept] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [karatage, setKaratage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const offsetRef = useRef(0);
  const paramsRef = useRef({ limit: DEFAULT_LIMIT, today_only: true });

  // Define headCells with conditional actions column
  const headCells = useMemo(() => {
    const baseCells = [
      // { id: 'id', label: 'ID' }, // Remove ID column
      { id: 'batch_number', label: 'Batch' },
      { id: 'product_code', label: 'Product Code' },
      { id: 'from_department', label: 'From Dept' },
      { id: 'to_department', label: 'To Dept' },
      { id: 'from_worker_code', label: 'From Worker' },
      { id: 'to_worker_code', label: 'To Worker' },
      { id: 'issue_weight', label: 'Issue Wt' },
      { id: 'received_weight', label: 'Received Wt' },
      { id: 'removal_weight', label: 'Removal Wt' },
      { id: 'karatage', label: 'Karatage' },
      { id: 'net_weight', label: 'Net Wt' },
      { id: 'stone_weight', label: 'Stone Wt' },
      { id: 'stone_qty', label: 'Stone Qty' },
      { id: 'dia_qty', label: 'Dia Qty' },
      { id: 'dia_broken', label: 'Dia Broken' },
      { id: 'color_stone_qty', label: 'CS Qty' },
      { id: 'color_stone_broken', label: 'CS Broken' },
      { id: 'nick_plate_qty', label: 'NP Qty' },
      { id: 'nick_plate_broken', label: 'NP Broken' },
      { id: 'pearl_weight', label: 'Pearl Wt' },
      { id: 'pearl_qty', label: 'Pearl Qty' },
      { id: 'pearl_weight_cts', label: 'Pearl Wt (cts)' },
      { id: 'remarks', label: 'Remarks' },
      { id: 'is_reverse_movement', label: 'Movement Type' },
      { id: 'created_by', label: 'Created By' },
      { id: 'created_at', label: 'Created At' }
    ];

    // Only add actions column for admin users
    if (user?.role === 'Admin') {
      baseCells.push({ id: 'actions', label: 'Actions', disableSort: true });
    }

    return baseCells;
  }, [user?.role]);

  const loadProcesses = useCallback(async (params = {}, options = {}) => {
    const append = Boolean(options.append);
    let requestParams;

    if (append) {
      const baseParams = paramsRef.current;
      const currentLimit = Number(baseParams.limit ?? DEFAULT_LIMIT);

      if (!currentLimit) {
        setHasMore(false);
        return [];
      }

      requestParams = {
        ...baseParams,
        offset: offsetRef.current,
      };
    } else {
      const mergedParams = {
        ...paramsRef.current,
        ...params,
      };

      Object.keys(mergedParams).forEach((key) => {
        if (mergedParams[key] === null || mergedParams[key] === undefined || mergedParams[key] === '') {
          delete mergedParams[key];
        }
      });

      if (params.limit !== undefined) {
        mergedParams.limit = params.limit;
      } else if (mergedParams.limit === undefined) {
        mergedParams.limit = DEFAULT_LIMIT;
      }

      if (mergedParams.today_only === undefined) {
        mergedParams.today_only = true;
      }

      paramsRef.current = mergedParams;
      requestParams = { ...paramsRef.current };
      offsetRef.current = 0;
      setHasMore(Number(requestParams.limit ?? DEFAULT_LIMIT) !== 0);
    }

    try {
      const data = await dispatch(fetchBatchProcesses(requestParams, { append }));
      const received = Array.isArray(data) ? data.length : 0;

      if (append) {
        offsetRef.current += received;
      } else {
        offsetRef.current = received;
      }

      const limitValue = Number(requestParams.limit ?? DEFAULT_LIMIT);

      if (!limitValue) {
        setHasMore(false);
      } else {
        setHasMore(received === limitValue);
      }

      return data;
    } catch (err) {
      setHasMore(false);
      return [];
    }
  }, [dispatch]);

  useEffect(() => {
    const initialise = async () => {
      await loadProcesses({ limit: DEFAULT_LIMIT, today_only: true });
    };

    initialise();
    dispatch(fetchDepartments());
    dispatch(fetchEmployees());
    dispatch(fetchPurities());
  }, [dispatch, loadProcesses]);

  useEffect(() => {
    if (error) {
      toast.error('Failed to fetch data.');
    }
  }, [error]);

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (result.isConfirmed) {
      await dispatch(deleteBatchProcess(id));
      setKaratage(null);
    }
  };
  
  const handleEdit = (id) => {
    navigate(`/transaction/inventory-process/${id}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Search and filter logic
  const filteredRows = useMemo(() => {
    let rows = processes;
    if (search) {
      const lower = search.toLowerCase();
      rows = rows.filter(row =>
        (row.batch_number && row.batch_number.toLowerCase().includes(lower)) ||
        (row.product_code && row.product_code.toLowerCase().includes(lower)) ||
        (row.from_department && row.from_department.toLowerCase().includes(lower)) ||
        (row.to_department && row.to_department.toLowerCase().includes(lower)) ||
        (row.remarks && row.remarks.toLowerCase().includes(lower))
      );
    }
    if (fromDate) {
      rows = rows.filter(row => row.created_at && new Date(row.created_at) >= new Date(fromDate));
    }
    if (toDate) {
      rows = rows.filter(row => row.created_at && new Date(row.created_at) <= new Date(toDate + 'T23:59:59'));
    }
    if (toDept) {
      rows = rows.filter(row => row.to_department && row.to_department === toDept.name);
    }
    if (employee) {
      rows = rows.filter(row =>
        (row.from_worker_code === employee.emp_code || row.to_worker_code === employee.emp_code)
      );
    }
    if (karatage) {
      rows = rows.filter(row => row.karatage && row.karatage === karatage.name);
    }
    return rows;
  }, [processes, search, fromDate, toDate, toDept, employee, karatage]);

  const sortedRows = useMemo(() => stableSort(filteredRows, getComparator(order, orderBy)), [filteredRows, order, orderBy]);
  const paginatedRows = useMemo(() => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sortedRows, page, rowsPerPage]);

  // Export to CSV
  const handleExport = () => {
    const csvRows = [
      headCells.filter(h => h.id !== 'actions').map(h => h.label).join(','),
      ...filteredRows.map(row => [
        // row.id, // Remove id from export
        row.batch_number,
        row.product_code,
        row.from_department,
        row.to_department,
        row.from_worker_code && row.from_worker_name ? `${row.from_worker_code} - ${row.from_worker_name}` : row.from_worker_name || '',
        row.to_worker_code && row.to_worker_name ? `${row.to_worker_code} - ${row.to_worker_name}` : row.to_worker_name || '',
        row.issue_weight,
        row.received_weight,
        row.removal_weight,
        row.karatage,
        (
          row.net_weight
          || (
            row.stone_weight !== undefined && row.pearl_weight !== undefined && row.from_department !== undefined
              ? (
                (row.enable_stone_cols === 1)
                  ? ((row.received_weight || 0) - ((row.stone_weight || 0) + (row.issue_weight || 0))).toFixed(3)
                  : (row.enable_pearl_cols === 1)
                    ? ((row.received_weight || 0) - ((row.pearl_weight || 0) + (row.issue_weight || 0))).toFixed(3)
                    : ((row.received_weight || 0) - (row.stone_weight || 0)).toFixed(3)
              )
              : ((row.received_weight || 0) - (row.stone_weight || 0)).toFixed(3)
          )
        ),
        row.stone_weight,
        row.stone_qty,
        row.dia_qty,
        row.dia_broken,
        row.color_stone_qty,
        row.color_stone_broken,
        row.nick_plate_qty,
        row.nick_plate_broken,
        row.pearl_weight,
        row.pearl_qty,
        row.pearl_weight_cts,
        row.remarks,
        row.is_reverse_movement,
        row.created_by_name,
        row.created_at ? new Date(row.created_at).toLocaleString() : ''
      ].map(val => `"${val ?? ''}"`).join(','))
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_process.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Clear filters handler
  const handleClearFilters = async () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setToDept(null);
    setEmployee(null);
    setKaratage(null);
    setPage(0);
    await loadProcesses({ limit: DEFAULT_LIMIT, today_only: true, date_from: null, date_to: null });
  };

  const handleApplyDateFilter = async () => {
    const params = { limit: DEFAULT_LIMIT };
    if (fromDate) {
      params.from_date = fromDate;
    } else {
      params.from_date = null;
    }
    if (toDate) {
      params.to_date = toDate;
    } else {
      params.to_date = null;
    }

    setPage(0);
    await loadProcesses({ ...params, today_only: false });
  };

  const handleLoadRecent = async () => {
    setFromDate('');
    setToDate('');
    setPage(0);
    await loadProcesses({ limit: DEFAULT_LIMIT, today_only: true, from_date: null, to_date: null });
  };

  const handleLoadAll = async () => {
    setPage(0);
    await loadProcesses({ limit: 0, today_only: false });
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await loadProcesses({}, { append: true });
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
       <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>Error: {error}</Typography>
    )
  }

  return (
    <Paper sx={{ p: 2, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2
        }}
      >
        <TextField
          label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', sm: 200 } }}
        />
        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 150 } }}
        />
        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 150 } }}
        />
        <Autocomplete
          options={departments}
          getOptionLabel={option => option.name}
          value={toDept}
          onChange={(_, newValue) => setToDept(newValue)}
          renderInput={params => <TextField {...params} label="To Department" size="small" sx={{ width: { xs: '100%', sm: 180 } }} />}
        />
        <Autocomplete
          options={employees}
          getOptionLabel={option => `${option.emp_code} - ${option.name}`}
          value={employee}
          onChange={(_, newValue) => setEmployee(newValue)}
          renderInput={params => <TextField {...params} label="Employee" size="small" sx={{ width: { xs: '100%', sm: 200 } }} />}
        />
        <Autocomplete
          options={purities}
          getOptionLabel={option => option.name}
          value={karatage}
          onChange={(_, newValue) => setKaratage(newValue)}
          renderInput={params => <TextField {...params} label="Karatage" size="small" sx={{ width: { xs: '100%', sm: 150 } }} />}
        />
        <Button variant="outlined" onClick={handleExport} sx={{ width: { xs: '100%', sm: 'auto' } }} startIcon={<DownloadIcon />}>Export CSV</Button>
        <Button variant="contained" color="secondary" onClick={handleClearFilters} sx={{ width: { xs: '100%', sm: 'auto' } }} startIcon={<ClearIcon />}>Clear Filters</Button>
        <Button variant="contained" color="primary" onClick={handleApplyDateFilter} sx={{ width: { xs: '100%', sm: 'auto' } }}>Apply Date Filter</Button>
        <Button variant="outlined" color="primary" onClick={handleLoadRecent} sx={{ width: { xs: '100%', sm: 'auto' } }}>Load Recent</Button>
        <Button variant="outlined" color="primary" onClick={handleLoadAll} sx={{ width: { xs: '100%', sm: 'auto' } }}>Load All Records</Button>
        {/* <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/transaction/inventory-process/add')}
          startIcon={<AddIcon />}
        >
          Add New Process
        </Button> */}
      </Box>
      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }} aria-label="inventory process table">
          <TableHead>
            <TableRow>
              {headCells.map(headCell => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                  align={headCell.id === 'actions' ? 'center' : 'left'}
                >
                  {headCell.disableSort ? headCell.label : (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={e => handleRequestSort(e, headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row.id}>
                {/* <TableCell>{row.id}</TableCell> Remove ID cell */}
                <TableCell>{row.batch_number}</TableCell>
                <TableCell>{row.product_code}</TableCell>
                <TableCell>{row.from_department}</TableCell>
                <TableCell>{row.to_department}</TableCell>
                <TableCell>{row.from_worker_code && row.from_worker_name ? `${row.from_worker_code} - ${row.from_worker_name}` : row.from_worker_name || ''}</TableCell>
                <TableCell>{row.to_worker_code && row.to_worker_name ? `${row.to_worker_code} - ${row.to_worker_name}` : row.to_worker_name || ''}</TableCell>
                <TableCell>{row.issue_weight}</TableCell>
                <TableCell>{row.received_weight}</TableCell>
                <TableCell>{row.removal_weight}</TableCell>
                <TableCell>{row.karatage}</TableCell>
                <TableCell>{
                  row.net_weight
                  || (
                    row.stone_weight !== undefined && row.pearl_weight !== undefined && row.from_department !== undefined
                      ? (
                        // If stone or pearl columns enabled, use received - (stone/pearl + issued)
                        (row.enable_stone_cols === 1)
                          ? ((row.received_weight || 0) - ((row.stone_weight || 0) + (row.issue_weight || 0))).toFixed(3)
                          : (row.enable_pearl_cols === 1)
                            ? ((row.received_weight || 0) - ((row.pearl_weight || 0) + (row.issue_weight || 0))).toFixed(3)
                            : ((row.received_weight || 0) - (row.stone_weight || 0)).toFixed(3)
                      )
                      : ((row.received_weight || 0) - (row.stone_weight || 0)).toFixed(3)
                  )
                }</TableCell>
                <TableCell>{row.stone_weight}</TableCell>
                <TableCell>{row.stone_qty}</TableCell>
                <TableCell>{row.dia_qty}</TableCell>
                <TableCell>{row.dia_broken}</TableCell>
                <TableCell>{row.color_stone_qty}</TableCell>
                <TableCell>{row.color_stone_broken}</TableCell>
                <TableCell>{row.nick_plate_qty}</TableCell>
                <TableCell>{row.nick_plate_broken}</TableCell>
                <TableCell>{row.pearl_weight}</TableCell>
                <TableCell>{row.pearl_qty}</TableCell>
                <TableCell>{row.pearl_weight_cts}</TableCell>
                <TableCell>{row.remarks}</TableCell>
                <TableCell>
                  {row.is_reverse_movement ? (
                    <Chip 
                      label="Reverse" 
                      color="warning" 
                      size="small" 
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <Chip 
                      label="Forward" 
                      color="success" 
                      size="small" 
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </TableCell>
                <TableCell>{row.created_by_name}</TableCell>
                <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</TableCell>
                {user?.role === 'Admin' && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleEdit(row.id)} startIcon={<EditIcon />}>Edit</Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(row.id)} startIcon={<DeleteIcon />}>Delete</Button>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading || loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default InventoryProcessTable; 